import type { CurrentUser } from "../api/currentUser";
import type { components } from "../api/generated-schema";

export type { CurrentUser };

export type ActorGrant = components["schemas"]["ActorGrant"];
export type AuthorizationScope = components["schemas"]["AuthorizationScope"];
export type ResourceScope = components["schemas"]["ResourceScope"];

/**
 * Đích phân quyền hiển thị (UX-only).
 *
 * - `id` xác định → phạm vi bản ghi (record-scoped: mở/sửa/xuất một hồ sơ cụ thể).
 * - `id` không xác định → phạm vi theo loại (type-scoped: danh sách, tạo mới, export).
 */
export type PermissionTarget = Readonly<{
  type: "MARTYR" | "CEMETERY" | "GRAVE" | "NCC_PROFILE" | "CASE";
  id?: string;
}>;

/**
 * Predicate quyền hiệu lực cho UI — **CHỈ phục vụ hiển thị**, không phải lớp bảo mật.
 *
 * Backend RBAC và data scope là điểm cưỡng chế duy nhất: mọi request vẫn đi qua
 * `apiRequest` bình thường và vẫn bị backend deny khi thiếu quyền/scope. Predicate
 * này chỉ quyết định có render action hay không; nó KHÔNG được mô tả là security
 * control và không được dùng để thay thế kiểm tra phía server.
 *
 * Quy tắc quyết định (deny by default):
 *
 * 1. Không có actor, không có `actionCodes`, hoặc `actionCode` rỗng → `false`.
 * 2. Một grant là *ứng viên* khi và chỉ khi `grant.actionCodes` chứa `actionCode`.
 *    Action code KHÔNG bao giờ được hợp nhất (union) giữa các grant: quyền luôn gắn
 *    với đúng tuple role–scope của chính grant đã khai action code đó.
 * 3. **Record-scoped** (`target.id` xác định): grant ứng viên đủ điều kiện khi và chỉ
 *    khi `grant.authorizationScope.dataScopes` chứa ref có
 *    `ref.type === target.type && ref.id === target.id`. Không có nhánh nào khác —
 *    chiều org/area KHÔNG thay thế được ref đúng id.
 * 4. **Type-scoped** (`target.id` không xác định — danh sách/tạo/export): grant ứng
 *    viên đủ điều kiện khi khai ít nhất một `dataScope` có `type === target.type`,
 *    HOẶC khai chiều org/area (`organizationId != null || administrativeAreaId != null`).
 * 5. Trả `true` khi và chỉ khi tồn tại ít nhất một grant ứng viên đủ điều kiện.
 *
 * Hàm tuyệt đối không so khớp trên `roleCode`: không có literal tên vai trò nào trong
 * file này, và số lượng grant (`grants.length`) không bao giờ là bằng chứng quyền.
 */
export function can(actor: CurrentUser, actionCode: string, target: PermissionTarget): boolean {
  // (1) Deny khi thiếu chủ thể hoặc thiếu action code cần kiểm tra.
  if (!actor) return false;
  if (typeof actionCode !== "string" || actionCode.length === 0) return false;
  if (!target) return false;

  // Số lượng grant không phải bằng chứng quyền — rỗng thì deny ngay.
  const grants = actor.grants;
  if (!Array.isArray(grants) || grants.length === 0) return false;

  const targetType = target.type;
  const targetId = target.id;

  return grants.some((grant) => grantQualifies(grant, actionCode, targetType, targetId));
}

/**
 * Kiểm tra xem actor có được gán actionCode cụ thể hay không (trên bất kỳ grant nào).
 * Không so roleCode, không union scope giữa các grant.
 */
export function hasAction(actor: CurrentUser | null | undefined, actionCode: string): boolean {
  if (!actor || !Array.isArray(actor.grants) || !actionCode) return false;
  return actor.grants.some(
    (grant) => Array.isArray(grant.actionCodes) && grant.actionCodes.includes(actionCode)
  );
}

/** Một grant đủ điều kiện khi action code thuộc CHÍNH grant đó và scope của nó khớp đích. */
function grantQualifies(
  grant: ActorGrant,
  actionCode: string,
  targetType: PermissionTarget["type"],
  targetId: string | undefined,
): boolean {
  // (2) Ứng viên: action code phải nằm trong grant này, không lấy từ grant khác.
  const codes = grant?.actionCodes;
  if (!Array.isArray(codes) || !codes.includes(actionCode)) return false;

  const scope: AuthorizationScope | undefined = grant?.authorizationScope;
  if (!scope) return false;

  const dataScopes = Array.isArray(scope.dataScopes) ? scope.dataScopes : [];

  if (targetId !== undefined) {
    // (3) Record-scoped: chỉ ref cùng type và ĐÚNG id mới đủ điều kiện.
    return dataScopes.some(
      (ref) => ref != null && ref.type === targetType && ref.id === targetId,
    );
  }

  // (4) Type-scoped: cùng type dữ liệu, hoặc có chiều org/area.
  return (
    dataScopes.some((ref) => ref != null && ref.type === targetType) ||
    scope.organizationId != null ||
    scope.administrativeAreaId != null
  );
}
